import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, ArrowRight, Users, Building2, Award, BookOpen, Target, Clock, Globe, Check, CalendarIcon } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { SEO } from "../components/SEO";
import axios from "axios";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InHouseTraining = () => {
  const [formData, setFormData] = useState({
    contact_name: "",
    organization: "",
    email: "",
    phone: "",
    industry: "",
    participants: "",
    training_topics: [],
    preferred_format: "",
    preferredDates: [],
    description: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);

  const trainingTopics = [
    { id: "iso9001", name: "ISO 9001 Quality Management", desc: "Foundation to Lead Auditor" },
    { id: "iso22000", name: "ISO 22000 Food Safety", desc: "FSMS implementation & auditing" },
    { id: "haccp", name: "HACCP Principles", desc: "Hazard analysis & critical control points" },
    { id: "gmp", name: "Good Manufacturing Practices", desc: "GMP compliance training" },
    { id: "gap", name: "Good Agricultural Practices", desc: "GAP & GlobalGAP standards" },
    { id: "internalaudit", name: "Internal Auditing", desc: "Audit skills & techniques" },
    { id: "foodsafety", name: "Food Safety Culture", desc: "Building safety-first mindset" },
    { id: "customized", name: "Customized Training", desc: "Tailored to your needs" }
  ];

  const industries = [
    "Food Manufacturing",
    "Agri-Business / Agriculture",
    "Energy",
    "Service Industry",
    "Government / Public Sector",
    "Healthcare",
    "SME / Small Business",
    "Other"
  ];

  const benefits = [
    { icon: Target, title: "Customized Content", desc: "Training tailored to your specific industry and organizational needs" },
    { icon: Users, title: "Team Building", desc: "Train your entire team together for consistent knowledge" },
    { icon: Clock, title: "Flexible Scheduling", desc: "Training at your preferred dates and times" },
    { icon: Building2, title: "On-Site or Virtual", desc: "Delivered at your location or online" },
    { icon: Award, title: "Certification Ready", desc: "Prepare your team for PECB certification exams" },
    { icon: Globe, title: "Cost Effective", desc: "Better value for groups vs. individual enrollment" }
  ];

  const handleTopicToggle = (topicId) => {
    setFormData(prev => ({
      ...prev,
      training_topics: prev.training_topics.includes(topicId)
        ? prev.training_topics.filter(t => t !== topicId)
        : [...prev.training_topics, topicId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.contact_name || !formData.email || !formData.organization) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (formData.training_topics.length === 0) {
      toast.error("Please select at least one training topic");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/inhouse-training-inquiry`, {
        ...formData,
        training_topics: formData.training_topics.map(id => 
          trainingTopics.find(t => t.id === id)?.name
        ).join(", "),
        preferred_dates: formData.preferredDates.length > 0 
          ? formData.preferredDates.map(d => format(d, 'PPP')).join(", ")
          : null
      });
      
      toast.success("Thank you! We'll contact you within 24 hours to discuss your training needs.");
      setFormData({
        contact_name: "",
        organization: "",
        email: "",
        phone: "",
        industry: "",
        participants: "",
        training_topics: [],
        preferred_format: "",
        preferredDates: [],
        description: ""
      });
    } catch (error) {
      toast.error("Failed to submit. Please try again or contact us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="inhouse-training-page">
      <SEO
        title="In-House Training | Compliance Management Systems"
        description="Customized on-site and virtual training for your organization. ISO 9001, ISO 22000, HACCP, GMP, food safety, internal auditing. Group training Jamaica & Caribbean."
        url="/services/in-house-training"
        keywords={["in-house training Jamaica", "corporate training food safety", "ISO training on-site", "customized compliance training", "team training Caribbean"]}
      />

      {/* Hero Section */}
      <section className="bg-navy-darker text-white py-20">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-gold/20 text-gold px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Users className="h-4 w-4" />
              Corporate Training
            </div>
            <h1 className="font-heading font-bold text-4xl lg:text-5xl mb-6">
              In-House Training
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed mb-8">
              Elevate your team's expertise with customized training programs delivered at your location or virtually. Our experienced trainers bring world-class education directly to your organization.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#request-form">
                <Button size="lg" className="bg-gold hover:bg-gold-dark text-navy-darker font-semibold">
                  Request In-House Training
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </a>
              <Link to="/courses">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                  View PECB Courses
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="font-heading font-bold text-3xl text-primary mb-4">
              Why Choose In-House Training?
            </h2>
            <p className="text-muted-foreground">
              Maximize your training investment with programs designed for your team
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <Card key={index} className="border-2 hover:border-brand/30 transition-colors">
                <CardContent className="pt-6">
                  <div className="w-12 h-12 bg-brand/10 rounded-xl flex items-center justify-center mb-4">
                    <benefit.icon className="h-6 w-6 text-brand" />
                  </div>
                  <h3 className="font-heading font-semibold text-lg mb-2">{benefit.title}</h3>
                  <p className="text-muted-foreground">{benefit.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Training Topics */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="font-heading font-bold text-3xl text-primary mb-4">
              Available Training Topics
            </h2>
            <p className="text-muted-foreground">
              Choose from our comprehensive range of training programs or request a customized curriculum
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {trainingTopics.map((topic) => (
              <div key={topic.id} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <BookOpen className="h-8 w-8 text-brand mb-4" />
                <h3 className="font-heading font-semibold mb-2">{topic.name}</h3>
                <p className="text-sm text-muted-foreground">{topic.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <p className="text-muted-foreground mb-4">
              Looking for PECB certified courses with individual enrollment?
            </p>
            <Link to="/courses">
              <Button variant="outline" className="border-brand text-brand hover:bg-brand hover:text-white">
                Browse PECB Training Courses
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Request Form */}
      <section className="py-20 bg-white" id="request-form">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <Card className="shadow-xl">
              <CardHeader className="bg-brand text-white rounded-t-lg">
                <CardTitle className="font-heading text-2xl text-center">
                  Request In-House Training
                </CardTitle>
                <p className="text-center text-white/80 mt-2">
                  Tell us about your training needs and we'll create a customized proposal
                </p>
              </CardHeader>
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="contact_name">Contact Name *</Label>
                      <Input
                        id="contact_name"
                        value={formData.contact_name}
                        onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                        placeholder="Your name"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="organization">Organization *</Label>
                      <Input
                        id="organization"
                        value={formData.organization}
                        onChange={(e) => setFormData({...formData, organization: e.target.value})}
                        placeholder="Company name"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        placeholder="your@email.com"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        placeholder="+1 (876) 000-0000"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="industry">Industry</Label>
                      <Select
                        value={formData.industry}
                        onValueChange={(value) => setFormData({...formData, industry: value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select your industry" />
                        </SelectTrigger>
                        <SelectContent>
                          {industries.map((ind) => (
                            <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="participants">Estimated Participants</Label>
                      <Select
                        value={formData.participants}
                        onValueChange={(value) => setFormData({...formData, participants: value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Number of participants" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="5-10">5-10 participants</SelectItem>
                          <SelectItem value="11-20">11-20 participants</SelectItem>
                          <SelectItem value="21-30">21-30 participants</SelectItem>
                          <SelectItem value="31-50">31-50 participants</SelectItem>
                          <SelectItem value="50+">50+ participants</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="preferred_format">Preferred Format</Label>
                    <Select
                      value={formData.preferred_format}
                      onValueChange={(value) => setFormData({...formData, preferred_format: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select delivery format" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="on-site">On-Site (at your location)</SelectItem>
                        <SelectItem value="virtual">Virtual (online live)</SelectItem>
                        <SelectItem value="hybrid">Hybrid (combination)</SelectItem>
                        <SelectItem value="flexible">Flexible / To be discussed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-3">
                    <Label>Training Topics of Interest *</Label>
                    <div className="grid md:grid-cols-2 gap-3">
                      {trainingTopics.map((topic) => (
                        <div
                          key={topic.id}
                          className={`flex items-start space-x-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                            formData.training_topics.includes(topic.id)
                              ? 'border-brand bg-brand/5'
                              : 'border-slate-200 hover:border-brand/50'
                          }`}
                          onClick={() => handleTopicToggle(topic.id)}
                        >
                          <div className={`h-4 w-4 shrink-0 rounded-sm border shadow flex items-center justify-center ${
                            formData.training_topics.includes(topic.id)
                              ? 'bg-primary border-primary text-primary-foreground'
                              : 'border-primary'
                          }`}>
                            {formData.training_topics.includes(topic.id) && (
                              <Check className="h-3 w-3" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-sm">{topic.name}</p>
                            <p className="text-xs text-muted-foreground">{topic.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Preferred Training Dates (Optional)</Label>
                    <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formData.preferredDates.length > 0 
                            ? formData.preferredDates.map(d => format(d, 'MMM d')).join(", ")
                            : "Select preferred dates"
                          }
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="multiple"
                          selected={formData.preferredDates}
                          onSelect={(dates) => setFormData({...formData, preferredDates: dates || []})}
                          disabled={(date) => date < new Date()}
                          initialFocus
                        />
                        <div className="p-2 border-t flex gap-2">
                          {formData.preferredDates.length > 0 && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="flex-1"
                              onClick={() => setFormData({...formData, preferredDates: []})}
                            >
                              Clear
                            </Button>
                          )}
                          <Button 
                            size="sm" 
                            className="flex-1 bg-brand hover:bg-brand-dark"
                            onClick={() => setCalendarOpen(false)}
                          >
                            Done
                          </Button>
                        </div>
                      </PopoverContent>
                    </Popover>
                    <p className="text-xs text-muted-foreground">Click to select multiple preferred dates for the training</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Additional Details</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      placeholder="Tell us about your training goals or any specific requirements..."
                      rows={4}
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-brand hover:bg-brand-dark text-white py-6 text-lg"
                    disabled={submitting}
                  >
                    {submitting ? "Submitting..." : "Request In-House Training"}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InHouseTraining;
